"use client";
import { useCancellationConfirmation } from "./cancellation-confirmation";
import { useEffect, useState, useTransition } from "react";
import {
  teacherCancellationDecisionAction,
  teacherCancellationListAction,
  requestStudentCancellationAction,
} from "./cancellation-actions";
type Rows = NonNullable<
  Awaited<ReturnType<typeof teacherCancellationListAction>>
>["data"];
export function TeacherCancellationPanel({ timezone }: { timezone: string }) {
  const { confirm, confirmationDialog } = useCancellationConfirmation();
  const [rows, setRows] = useState<Rows>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [pending, start] = useTransition();
  useEffect(() => {
    let active = true;
    teacherCancellationListAction({}).then((result) => {
      if (active) {
        if (result?.data) setRows(result.data);
        else setError("取消待辦讀取失敗，請重新整理");
      }
    }).catch(() => { if (active) setError("取消待辦讀取失敗，請重新整理"); }).finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
    };
  }, []);
  return (
    <section className="booking-card" aria-label="取消判定待辦">
      <h3>取消判定待辦 · {rows?.length ?? 0}</h3>
      <p>時段已釋放，堂數暫時保留。請判定是否扣堂。</p>
      {error && <p role="alert">{error}</p>}
      {loading && <p role="status">正在讀取取消申請…</p>}
      {!loading && !error && !rows?.length && <p>目前沒有待判定申請</p>}
      {!selectedId && <div className="teacher-desk-list">{rows?.map(row => <button type="button" key={row.id} onClick={() => setSelectedId(row.id)}><span><strong>{row.memberName} · {row.courseName}</strong><small>{new Intl.DateTimeFormat('zh-TW', { timeZone: timezone, dateStyle: 'short', timeStyle: 'short', hour12: false }).format(new Date(row.startsAt))}</small><small>{row.venueName} · {row.creditCost} 堂待判定</small></span><b>›</b></button>)}</div>}
      {selectedId && <button type="button" className="sale-secondary-button" disabled={pending} onClick={() => setSelectedId(null)}>‹ 返回申請清單</button>}
      {rows?.filter(row => row.id === selectedId).map((row) => (
        <form
          key={row.id}
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            if (
              !(await confirm(
                `確認${form.get("decision") === "REFUND" ? "回補堂數" : "扣堂"}？完成後不能直接改寫。`,
              ))
            )
              return;
            start(async () => {
              const result = await teacherCancellationDecisionAction({
                bookingId: row.bookingId,
                decision: String(form.get("decision")) as "REFUND" | "DEDUCT",
                reason: String(form.get("reason")),
              });
              if (result?.data) {
                setRows((current) =>
                  current?.filter((item) => item.id !== row.id),
                );
                setError("");
                setSelectedId(null);
              } else setError(result?.serverError ?? "判定失敗，請重新確認。");
            });
          }}
        >
          <strong>
            {row.memberName} · {row.courseName}
          </strong>
          <p>
            {new Intl.DateTimeFormat("zh-TW", {
              timeZone: timezone,
              dateStyle: "short",
              timeStyle: "short",
              hour12: false,
            }).format(new Date(row.startsAt))}{" "}
            · {row.venueName} · {row.creditCost} 堂
          </p>
          <p>{row.reason}</p>
          <div className="form-grid">
            <label>
              判定
              <select name="decision">
                <option value="REFUND">回補堂數</option>
                <option value="DEDUCT">扣堂</option>
              </select>
            </label>
            <label>
              原因
              <input name="reason" required maxLength={1000} />
            </label>
          </div>
          <button disabled={pending}>確認判定</button>
        </form>
      ))}
      {confirmationDialog}
    </section>
  );
}
export function StudentCancellationButton({
  bookingId,
}: {
  bookingId: string;
}) {
  const { confirm, confirmationDialog } = useCancellationConfirmation();
  const [message, setMessage] = useState("");
  const [pending, start] = useTransition();
  const [done, setDone] = useState(false);
  return (
    <div>
      <button
        type="button"
        disabled={pending || done}
        onClick={async () => {
          if (
            !(await confirm(
              "確認取消這筆預約？期限內取消可能需由老師判定是否扣堂。",
            ))
          )
            return;
          start(async () => {
            const result = await requestStudentCancellationAction({
              bookingId,
            });
            if (result?.data) {
              setDone(true);
              setMessage(
                result.data.disposition === "PENDING_REVIEW"
                  ? "已取消，堂數保留中，等待老師判定。"
                  : "預約已取消。",
              );
            } else setMessage(result?.serverError ?? "取消失敗，請重新確認。");
          });
        }}
      >
        {pending ? "處理中…" : "取消預約"}
      </button>
      {message && <p role="status">{message}</p>}
      {confirmationDialog}
    </div>
  );
}
